import React, { useState, useEffect, useRef, useCallback } from 'react';
import { IconSettings } from '@tabler/icons-react';
import { ButtonContent } from './ui/ButtonContent';
import { MicIcon } from './icons/MicIcon';
import { VideoIcon } from './icons/VideoIcon';
import VideoPreview from './VideoPreview';
import { getMediaStreamWithFallback } from '../lib/media-utils';
import { captureException } from '../lib/telemetry/posthog/frontend';
import { getAudioContextConstructor } from '../lib/utils/audio-context';
import { computeMicLevel, smoothMicLevel } from '../lib/utils/audio-level';
import { useMediaPermission } from './hooks/useMediaPermission';

interface MediaSetupProps {
  videoEnabled: boolean;
  onMicrophoneSelect: (deviceId: string) => void;
  onSpeakerSelect?: (deviceId: string) => void;
  onCameraSelect?: (deviceId: string) => void;
  /** Called when camera availability changes (denied/unavailable -> false). */
  onCameraToggle?: (enabled: boolean) => void;
  /** When true, camera is mandatory: a denied camera blocks the join. */
  requireVideo?: boolean;
  /**
   * Defer the permission prompt until the access requester is invoked (for
   * example on the Start button), instead of prompting on mount.
   */
  deferPermission?: boolean;
  /**
   * Receives the imperative access requester so a parent Start control can
   * trigger the prompt on a user gesture. Resolves true once access is granted
   * (and, when {@link requireVideo} is set, the camera is available too). Pass
   * `{ skipVideo: true }` to join audio-only (no camera prompt), for the
   * "Or join without video" path on optional-video sessions.
   */
  onAccessRequesterReady?: (request: (opts?: { skipVideo?: boolean }) => Promise<boolean>) => void;
}

/**
 * Lobby device setup for video conversations. Mirrors MicrophoneSetup: the
 * landing surface is a single "Test devices" affordance, and the camera preview
 * plus device pickers live inside the test modal. Permission is requested on the
 * Start gesture (or when opening Test devices), never on mount.
 */
const MediaSetup: React.FC<MediaSetupProps> = ({
  videoEnabled,
  onMicrophoneSelect,
  onCameraSelect,
  onCameraToggle,
  requireVideo = false,
  deferPermission = false,
  onAccessRequesterReady,
}) => {
  const [volume, setVolume] = useState(0);
  const [showTestModal, setShowTestModal] = useState(false);
  /** True when the mic was granted but a mandatory camera was denied at Start. */
  const [cameraRequiredError, setCameraRequiredError] = useState(false);
  const [isRetryingCamera, setIsRetryingCamera] = useState(false);

  const [selectedCameraId, setSelectedCameraId] = useState<string>('');

  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const rafRef = useRef<number | null>(null);
  /**
   * Tracks whether the most recent camera acquisition failed. Set synchronously
   * inside requestVideo so the deferred Start gesture can decide, the moment the
   * request settles, whether a mandatory camera is actually available.
   */
  const videoDeniedRef = useRef(false);
  /**
   * Set true for the "Or join without video" path so the camera probe is
   * skipped entirely (no camera prompt). The guest can still enable video from
   * the in-call control bar, which acquires the camera on demand.
   */
  const skipVideoRef = useRef(false);

  /**
   * Probes the camera after audio is granted so the Start gesture knows whether
   * a mandatory camera is available. This is a probe only: the stream is stopped
   * immediately (the test modal's VideoPreview acquires its own when opened), so
   * the camera light never lingers when the user joins without opening the modal.
   * Skipped when joining audio-only. Failure is non-fatal for audio: it just
   * marks the camera unavailable and notifies the parent. Errors are swallowed
   * here so the shared permission hook does not treat them as a mic failure.
   */
  const requestVideo = useCallback(async () => {
    if (!videoEnabled || skipVideoRef.current) return;

    videoDeniedRef.current = false;
    try {
      const videoStream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: 'user' },
      });
      videoStream.getTracks().forEach((t) => t.stop());
    } catch (videoErr: unknown) {
      videoDeniedRef.current = true;
      console.warn('Camera permission denied or unavailable:', videoErr);
      const err = videoErr as { name?: string; message?: string } | null | undefined;
      const isDismissed =
        err?.name === 'NotAllowedError' && err?.message === 'Permission dismissed';

      // Notify the parent the camera is unavailable (drives the in-call default
      // and, when required, the Start gating). Hard denials are reportable;
      // dismissals are expected user hesitation.
      onCameraToggle?.(false);
      if (!requireVideo && !isDismissed) {
        captureException(
          videoErr instanceof Error ? videoErr : String(videoErr),
          'MediaSetup.requestVideoPermission',
          {},
        );
      }
    }
  }, [videoEnabled, requireVideo, onCameraToggle]);

  /**
   * Picks a default camera from the enumerated device list once audio
   * permission has unlocked device labels.
   */
  const selectDefaultCamera = useCallback(
    (allDevices: MediaDeviceInfo[]) => {
      const videoInputs = allDevices.filter((d) => d.kind === 'videoinput');

      if (videoInputs.length > 0 && onCameraSelect) {
        const defaultCamera = videoInputs.find((d) => d.deviceId === 'default') || videoInputs[0];
        setSelectedCameraId(defaultCamera.deviceId);
        onCameraSelect(defaultCamera.deviceId);
      }
    },
    [onCameraSelect],
  );

  const {
    devices: micDevices,
    selectedId: selectedMicId,
    setSelectedId: setSelectedMicId,
    permissionError: micPermissionError,
    permissionDismissed,
    unsupportedEnvironment,
    retryHint,
    isRetrying,
    granted,
    requestAccess,
    handleEnableAccess,
    handleRetryFromDismissed,
  } = useMediaPermission({
    onDeviceSelect: onMicrophoneSelect,
    context: 'MediaSetup',
    onAudioGranted: requestVideo,
    onDevicesEnumerated: selectDefaultCamera,
    deferUntilRequested: deferPermission,
  });

  /**
   * Access requester for the Start gesture. Grants when the mandatory mic is
   * available; when the host requires the camera, a denied/unavailable camera
   * also blocks the join (the lobby then surfaces the camera error for retry).
   * With `{ skipVideo: true }` it requests audio only (no camera prompt) and
   * marks the camera off so the call starts video-paused but can be turned on
   * from the in-call control bar.
   */
  const requestAccessForStart = useCallback(async (opts?: { skipVideo?: boolean }): Promise<boolean> => {
    skipVideoRef.current = !!opts?.skipVideo;
    const ok = await requestAccess();
    if (!ok) return false;
    if (opts?.skipVideo) {
      // Audio-only join: start with the camera off (enableable mid-call).
      onCameraToggle?.(false);
      setCameraRequiredError(false);
      return true;
    }
    if (requireVideo && videoDeniedRef.current) {
      // Mic is fine, so the mic error UI never shows: surface a camera-specific
      // notice so a denied mandatory camera is not a silent no-op.
      setCameraRequiredError(true);
      return false;
    }
    setCameraRequiredError(false);
    return true;
  }, [requestAccess, requireVideo, onCameraToggle]);

  // Hand the access requester to the parent so the Start gesture can prompt.
  useEffect(() => {
    onAccessRequesterReady?.(requestAccessForStart);
  }, [onAccessRequesterReady, requestAccessForStart]);

  /** Re-probes the camera after a mandatory-camera denial; clears the notice if it is now available. */
  const handleRetryCamera = useCallback(async () => {
    setIsRetryingCamera(true);
    try {
      await requestAccess();
      if (!videoDeniedRef.current) setCameraRequiredError(false);
    } finally {
      setIsRetryingCamera(false);
    }
  }, [requestAccess]);

  // Audio visualization stream
  useEffect(() => {
    if (!selectedMicId) return undefined;

    /** Set to true by the cleanup function so an in-flight getMediaStreamWithFallback
     * result knows the component is gone and must not keep the mic open. */
    let cancelled = false;

    const startStream = async () => {
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      if (audioContextRef.current) void audioContextRef.current.close();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);

      try {
        const result = await getMediaStreamWithFallback({
          deviceId: selectedMicId,
          kind: 'audio',
          constraints: {
            autoGainControl: false,
            echoCancellation: false,
            noiseSuppression: false,
          },
          context: 'media_setup_metering',
        });

        // Component unmounted while the promise was in flight: stop every
        // acquired track immediately so the mic/camera light does not linger.
        if (cancelled) {
          result.stream.getTracks().forEach((t) => t.stop());
          return;
        }

        const stream = result.stream;
        streamRef.current = stream;

        if (result.fallbackUsed && result.actualDeviceId) {
          setSelectedMicId(result.actualDeviceId);
        }

        const ctx = new (getAudioContextConstructor())();
        audioContextRef.current = ctx;
        const source = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 1024;
        analyser.smoothingTimeConstant = 0.5;
        source.connect(analyser);

        // Time-domain samples drive an RMS level (see computeMicLevel).
        const dataArray = new Uint8Array(analyser.fftSize);

        const animate = () => {
          if (cancelled) return;
          analyser.getByteTimeDomainData(dataArray);
          const level = computeMicLevel(dataArray);
          setVolume((prev) => smoothMicLevel(prev, level));
          rafRef.current = requestAnimationFrame(animate);
        };
        animate();
      } catch (e) {
        console.error('Error accessing microphone for setup:', e);
        captureException(e instanceof Error ? e : String(e), 'MediaSetup.getAccess', { selectedMicId });
      }
    };
    void startStream();

    return () => {
      cancelled = true;
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        void audioContextRef.current.close();
      }
    };
  }, [selectedMicId, setSelectedMicId]);

  const handleCameraChange = (deviceId: string) => {
    setSelectedCameraId(deviceId);
    if (onCameraSelect) {
      onCameraSelect(deviceId);
    }
  };

  /**
   * Opens the device test modal, first prompting for access if it has not yet
   * been granted (deferred mode). Testing devices is an explicit user action,
   * so requesting permission here does not reintroduce on-landing friction.
   */
  const handleOpenTestModal = useCallback(async () => {
    if (!granted) {
      const ok = await requestAccess();
      if (!ok) return;
    }
    setShowTestModal(true);
  }, [granted, requestAccess]);

  if (unsupportedEnvironment) {
    return (
      <div className="w-full">
        <div className="flex flex-col items-center justify-center gap-3 p-6 text-center">
          <p className="text-lg text-muted-foreground">
            {videoEnabled ? 'Microphone and camera access' : 'Microphone access'} isn't supported in
            your current browser environment. Please open this link directly in Chrome or Firefox to
            continue.
          </p>
        </div>
      </div>
    );
  }

  if (permissionDismissed) {
    return (
      <div className="w-full">
        <div className="flex flex-col items-center justify-center gap-3 p-6 text-center">
          <p className="text-lg text-muted-foreground">
            {videoEnabled ? 'Microphone and camera access' : 'Microphone access'} is needed to start.
          </p>
          <button
            type="button"
            onClick={handleRetryFromDismissed}
            className="px-4 py-2 rounded-lg bg-volt/15 hover:bg-volt/15 text-foreground text-sm transition-colors"
          >
            Allow access
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {micPermissionError ? (
        <div className="bg-wash-danger rounded-lg p-3 text-foreground space-y-3">
          <p className="text-lg font-medium">{videoEnabled ? 'Microphone and camera access is required to join.' : 'Microphone access is required to join.'}</p>
          <p className="text-muted-foreground text-sm">Enable access in your browser, then retry.</p>
          {retryHint && (
            <p className="text-muted-foreground text-sm">{retryHint}</p>
          )}
          <button
            type="button"
            onClick={handleEnableAccess}
            disabled={isRetrying}
            aria-busy={isRetrying}
            className="px-3 py-2 rounded-lg button-accent font-medium disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            <ButtonContent loading={isRetrying}>Enable Access</ButtonContent>
          </button>
        </div>
      ) : cameraRequiredError ? (
        <div className="bg-wash-danger rounded-lg p-3 text-foreground space-y-3">
          <p className="text-lg font-medium">Camera access is required to join.</p>
          <p className="text-muted-foreground text-sm">Enable camera access in your browser, then retry.</p>
          <button
            type="button"
            onClick={() => { void handleRetryCamera(); }}
            disabled={isRetryingCamera}
            aria-busy={isRetryingCamera}
            className="px-3 py-2 rounded-lg button-accent font-medium disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            <ButtonContent loading={isRetryingCamera}>Enable Access</ButtonContent>
          </button>
        </div>
      ) : (
        /* Quiet settings affordance pinned to the card's lower corner: a cog
           plus camera/mic glyphs that open the device test panel. */
        <button
          type="button"
          onClick={() => { void handleOpenTestModal(); }}
          aria-label="Test devices"
          title="Test devices"
          className="absolute bottom-4 right-4 flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-surface-hover/60 transition-colors"
        >
          <IconSettings className="w-5 h-5" aria-hidden="true" />
          {videoEnabled && <VideoIcon className="w-5 h-5" aria-hidden="true" />}
          <MicIcon className="w-5 h-5" aria-hidden="true" />
        </button>
      )}

      {/* Test Devices Modal: camera preview (video sessions) plus mic + level meter. */}
      {showTestModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="w-full max-w-md bg-background border border-border rounded-2xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-foreground">Test Your Devices</h3>
              <button
                type="button"
                onClick={() => setShowTestModal(false)}
                aria-label="Close"
                className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-surface-hover transition-colors"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              {/* Camera preview + selector (video sessions only) */}
              {videoEnabled && (
                <VideoPreview
                  onDeviceSelect={handleCameraChange}
                  selectedDeviceId={selectedCameraId}
                  requireVideo={requireVideo}
                  onCameraAvailable={requireVideo ? onCameraToggle : undefined}
                />
              )}

              {/* Mic selector */}
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2 text-left">Microphone</label>
                <div className="relative">
                  <select
                    value={selectedMicId}
                    onChange={(e) => setSelectedMicId(e.target.value)}
                    className="w-full bg-background border border-border text-muted-foreground rounded-lg px-4 py-3 pl-10 appearance-none focus:ring-1 focus:ring-volt focus:border-volt focus:outline-none cursor-pointer transition-colors"
                  >
                    {micDevices.map((d) => (
                      <option key={d.deviceId} value={d.deviceId}>
                        {d.label || `Microphone ${d.deviceId.slice(0, 5)}...`}
                      </option>
                    ))}
                  </select>
                  <MicIcon className="absolute left-3 top-3.5 w-5 h-5 text-subtle-foreground pointer-events-none" />
                </div>
              </div>

              {/* Audio level visualizer */}
              <div>
                <div className="text-xs text-subtle-foreground mb-1 text-left">
                  Input Level
                </div>
                <div className="h-2 bg-surface-raised rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-colors duration-100 ease-out ${volume > 80 ? 'bg-danger' : 'bg-success'}`}
                    style={{ width: `${volume}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaSetup;
